import { useQuery } from '@apollo/client';
import styled from '@emotion/styled';
import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTableUniverse } from '~Components/TableUniverse';
import Loader from '~ComponentsImproved/Loader';
import Icon from '~ComponentsImproved/Icon';
import CloseIcon from '~Assets/icons/close.svg';
import { insideWrapperStyles } from '~ComponentsImproved/Modal';
import { useButton, useI18n, useSelector } from '~Hooks';
import {
  TablesAndFloorPlansQuery,
  TablesAndFloorPlansQueryVariables,
  TablesAndFloorPlansQuery_venue_floorPlans_tables as TableType,
} from '~Scenes/Handy/__queries__';
import { TABLES_QUERY } from '~Scenes/Handy/queries';
import {
  sortTablesByCode,
  TableComponentProps,
  TableKey,
} from '~Scenes/Handy/Scenes/TableSelector/types';
import Table from '~Scenes/Handy/Scenes/TableSelector/Table';
import TableLayout from '~Scenes/Handy/Scenes/TableSelector/TableLayout';
import { Box, TransparentButton } from '~Styles';
import { useSwipeable } from 'react-swipeable';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 99;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background-color: var(--colorBlueGray1300);
  opacity: 0.75;
`;

const ModalWrapper = styled(Box)`
  box-shadow: ${insideWrapperStyles.boxShadow};
  border-radius: ${insideWrapperStyles.borderRadius};
  background-color: ${insideWrapperStyles.backgroundColor};
  height: calc(100vh - 16px);
  width: calc(100vw - 16px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  z-index: 1;
`;

const TopBar = styled(Box)`
  display: flex;
  justify-content: flex-end;
  flex-shrink: 0;
`;

const CloseButton = styled(TransparentButton)`
  border-radius: 100%;
  height: 40px;
  width: 40px;
  min-width: 40px;
  min-height: 40px;
  padding: 0;
  flex-shrink: 0;
`;

const TablesWrapper = styled.div`
  flex: 1;
  overflow-y: scroll;
  // Hide scrollbar
  -ms-overflow-style: none;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
  box-shadow: 0px 0px 64px 0px rgba(0, 0, 0, 0.32);
`;

interface Props {
  onSelectTable: (table: TableType) => void;
  onClose: () => void;
}

const HandyTables: FC<Props> = ({ onSelectTable, onClose }) => {
  const closeButton = useButton({
    onClick: onClose,
  });
  const universe = useTableUniverse();

  const demoMode = useSelector((state) => state.demoMode);
  let venueId = useSelector((state) => state.venueId);
  if (demoMode) {
    venueId = window.DEMO_MODE_VENUE_ID;
  }

  const [selectedTableId, setSelectedTableId] = useState<string | null>(null);
  const isScrolling = useRef(false);

  const handlers = useSwipeable({
    delta: 30,
    onSwiping: () => {
      isScrolling.current = true;
    },
  });

  const tablesQuery = useQuery<TablesAndFloorPlansQuery, TablesAndFloorPlansQueryVariables>(
    TABLES_QUERY,
    {
      variables: {
        id: venueId || '',
      },
      fetchPolicy: 'cache-and-network',
      skip: !venueId,
    },
  );

  const allTables =
    tablesQuery.data?.venue.floorPlans
      ?.flatMap((floorPlan) => floorPlan.tables)
      .filter((table) => !table.deleted) || [];

  // Sort the tables by code for presentation
  const allTablesSorted = useMemo(() => sortTablesByCode(allTables), [allTables]);

  const floorPlans = useMemo(() => {
    return (
      tablesQuery.data?.venue.floorPlans.map((floorPlan) => ({
        ...floorPlan,
        tables: floorPlan.tables.filter(
          (table) => !table.deleted || universe.cheques.find((c) => c.tableId === table.id),
        ),
      })) || []
    );
  }, [universe.cheques, tablesQuery.data]);

  const onTouchStart = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    e.stopPropagation();
  }, []);

  const onTouchEnd = useCallback(
    (table: TableType) => {
      if (!isScrolling.current) {
        setSelectedTableId(table.id);
        onSelectTable(table);
      }

      // Prevent any accidental mini-touches
      setTimeout(() => {
        isScrolling.current = false;
      }, 100);
    },
    [onSelectTable],
  );

  const TableComponent = ({ table, keyType, isMapView }: TableComponentProps) => {
    return (
      <Table
        key={`${TableKey[keyType]}_${table.id}_copy`}
        keyType={keyType}
        table={table}
        selected={selectedTableId === table.id}
        onSelectCheque={() => {}}
        onSelectTable={(selectedTable) => {
          setSelectedTableId(selectedTable.id);
          onSelectTable(selectedTable);
        }}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        courseTypes={tablesQuery.data?.venue.company.courseTypes}
        isMapView={isMapView}>
        {table.code}
      </Table>
    );
  };

  return createPortal(
    <Overlay>
      <Backdrop onTouchStart={onClose} />
      <ModalWrapper>
        <TopBar>
          <CloseButton display="flex" alignItems="center" justifyContent="center" {...closeButton}>
            <Icon src={CloseIcon} color="var(--colorBlueGray600)" width="24px" height="24px" />
          </CloseButton>
        </TopBar>
        <TablesWrapper id="tables-wrapper-copy" {...handlers}>
          {tablesQuery.loading ? (
            <Box
              style={{ height: '50vh' }}
              display="flex"
              alignItems="center"
              flexDirection="column"
              gap="small"
              justifyContent="center">
              <Loader />
            </Box>
          ) : (
            <TableLayout
              key="handy-table-selector-copy"
              allTablesSorted={allTablesSorted}
              floorPlans={floorPlans}
              chequeIsDragging={false}
              selectedTableView="list"
              tableComponent={TableComponent}
            />
          )}
        </TablesWrapper>
      </ModalWrapper>
    </Overlay>,
    document.body,
  );
};

export default HandyTables;
